import { Button } from '@creditornot/cb-button';
import { FormRow } from '@creditornot/cb-form';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { Input } from '@creditornot/cb-input';
import { Modal, ModalContent, ModalFooter, ModalHeader } from '@creditornot/cbt-modal';
import { useMutation } from '@tanstack/react-query';
import React from 'react';
import { Field, Form } from 'react-final-form';
import toast from 'react-hot-toast';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import * as yup from 'yup';

import { APIErrorNotification, RFFFieldErrorWrapper } from 'common/components';
import { Typography } from 'common/typography';
import { localize } from 'common/utils';
import validateYupSchema from 'common/utils/validateYupSchema';
import { sharedMenuInteractionClickRenameMenu } from 'core/telemetry/avo/Avo';

import { updateSharedMenuInfo } from '../../../api';
import { CustomToast } from '../../../components/CustomToast';
import { SHARED_MENU_NAME_MAX_LENGTH } from '../../../constants';
import { useScopeContext } from '../../../contexts/ScopeContext';
import { useSelectionData } from '../../../hooks/useSelectionData';
import { useSharedMenuData } from '../../../hooks/useSharedMenuData';
import { EditSharedMenuInfoPayload, SharedMenu } from '../../../types';
import { updatePrimaryLanguageTranslation } from '../../SharedMenuEditorPage/utils';

type RenameSharedMenuModalProps = {
  onClose: () => void;
  isOpen: boolean;
  sharedMenu: SharedMenu;
};

type MutationVariables = { sharedMenuId: string; payload: EditSharedMenuInfoPayload };

const StyledModalContent = styled(ModalContent)`
  margin-block-start: 16px;
  padding: 0;
  border-block-start: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const StyledModalHeader = styled(ModalHeader)`
  margin-block-end: 16px;
`;

const ModalSubheader = styled(Typography.Body3)`
  margin: 16px 24px;
`;

const StyledModalFooter = styled(ModalFooter)`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ModalButtons = styled.div`
  display: flex;
  width: 100%;
  justify-content: flex-end;
  gap: 8px;
`;

type FormValues = {
  name: string;
  language: Language | undefined;
};

const validationSchema = yup.object({
  name: yup
    .string()
    .required()
    .lowercase()
    .trim()
    .max(SHARED_MENU_NAME_MAX_LENGTH)
    .test(
      'name',
      { id: 'shared-menu.validation-messages.menu.empty-name' },
      (value: string | undefined) => typeof value === 'string' && value !== '',
    )
    .when('$originalMenuName', (originalMenuName: string, fieldSchema: yup.StringSchema) =>
      fieldSchema.test(
        'name',
        { id: 'shared-menu.validation-messages.menu.same-menu-name' },
        value => originalMenuName.toLocaleLowerCase() !== value,
      ),
    )
    .when('$allOtherSharedMenusNames', (allOtherSharedMenusNames: string[], fieldSchema: yup.StringSchema) =>
      fieldSchema.test('name', { id: 'shared-menu.validation-messages.menu.duplicated-name' }, value =>
        typeof value === 'string' ? !allOtherSharedMenusNames.includes(value) : true,
      ),
    ),
});

const RENAME_SHARED_MENU_TOAST_ID = 'RENAME_SHARED_MENU_TOAST_ID';

export const RenameSharedMenuModal = ({ isOpen, onClose, sharedMenu }: RenameSharedMenuModalProps) => {
  const scope = useScopeContext();
  const { refetchSharedMenus, sharedMenus } = useSharedMenuData({ scope });
  const { selection, loadingInitialSelectionData } = useSelectionData();
  const { formatMessage: t } = useIntl();

  const originalMenuName = localize(sharedMenu.name, sharedMenu.primary_language);
  const allOtherSharedMenusNames = sharedMenus
    .map(sm => localize(sm.name, sm.primary_language).toLocaleLowerCase())
    .filter(sm => sm !== originalMenuName.toLocaleLowerCase());

  const {
    mutate: executeRenameMenu,
    isLoading,
    error,
  } = useMutation(({ sharedMenuId, payload }: MutationVariables) => updateSharedMenuInfo(sharedMenuId, payload), {
    onSuccess: async ({ id: sharedMenuId }, { payload: { name, primary_language } }) => {
      sharedMenuInteractionClickRenameMenu({ sharedMenuId });
      const renamedMenuName = localize(name, primary_language);
      toast.custom(
        <CustomToast
          variant="success"
          title={t({ id: 'shared-menu.rename.toasts.success.title' })}
          message={
            <FormattedMessage
              id="shared-menu.rename.toasts.success.message"
              values={{ originalMenuName, renamedMenuName, strong: chunks => <strong>{chunks}</strong> }}
            />
          }
          toastId={RENAME_SHARED_MENU_TOAST_ID}
        />,
        { id: RENAME_SHARED_MENU_TOAST_ID },
      );
      onClose();
      refetchSharedMenus();
    },
    onError: (_, { payload: { name, primary_language } }) => {
      const renamedMenuName = localize(name, primary_language);
      toast.custom(
        <CustomToast
          variant="error"
          title={t({ id: 'shared-menu.rename.toasts.error.title' })}
          message={
            <FormattedMessage
              id="shared-menu.rename.toasts.error.message"
              values={{ originalMenuName, renamedMenuName, strong: chunks => <strong>{chunks}</strong> }}
            />
          }
          toastId={RENAME_SHARED_MENU_TOAST_ID}
        />,
        { id: RENAME_SHARED_MENU_TOAST_ID, duration: Infinity },
      );
    },
  });

  return (
    <Modal open={isOpen} onDismiss={onClose} sizePreset={'centered:medium'} disableContentPadding>
      <StyledModalHeader onCloseClick={onClose}>
        <FormattedMessage id="shared-menu.rename.label" />
      </StyledModalHeader>
      <ModalSubheader>
        <FormattedMessage id="shared-menu.rename.subheader" />
      </ModalSubheader>
      <Form<FormValues>
        initialValues={{
          name: originalMenuName,
        }}
        validate={validateYupSchema(validationSchema, {
          originalMenuName,
          allOtherSharedMenusNames,
        })}
        onSubmit={values => {
          const payload: EditSharedMenuInfoPayload = {
            name: updatePrimaryLanguageTranslation(sharedMenu.primary_language, values.name, sharedMenu.name),
            primary_language: sharedMenu.primary_language,
            categoryIds: selection.categories.map(category => category.id),
          };

          executeRenameMenu({ sharedMenuId: sharedMenu.id, payload });
        }}
      >
        {({ handleSubmit, pristine, hasValidationErrors }) => (
          <form onSubmit={handleSubmit}>
            <StyledModalContent>
              <FormRow label={t({ id: 'shared-menu.new-shared-menu.name' })} horizontalPadding>
                <Field<FormValues['name']> name="name">
                  {field => (
                    <RFFFieldErrorWrapper field={field}>
                      {input => <Input {...input} placeholder={originalMenuName} />}
                    </RFFFieldErrorWrapper>
                  )}
                </Field>
              </FormRow>
            </StyledModalContent>
            <StyledModalFooter>
              {!!error && <APIErrorNotification error={error} style={{ width: '100%' }} />}
              <ModalButtons>
                <Button variant="secondary" size="small" onClick={onClose}>
                  <FormattedMessage id="state.cancel" />
                </Button>
                <Button
                  variant="primary"
                  size="small"
                  type="submit"
                  disabled={pristine || hasValidationErrors || isLoading || loadingInitialSelectionData}
                  loading={isLoading}
                >
                  <FormattedMessage id="shared-menu.rename.label" />
                </Button>
              </ModalButtons>
            </StyledModalFooter>
          </form>
        )}
      </Form>
    </Modal>
  );
};
